import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  CircularProgress,
  Stack,
  Fade,
  Avatar,
  Divider,
  Chip,
  Grid,
  InputAdornment,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../services/auth/AuthContext";
import { authApi } from "../../services/auth/authApi";

// Helper function to calculate relative registration duration in Persian
function formatMemberDuration(createdAt) {
  if (!createdAt) return "مشخص نشده";

  const createdDate = new Date(createdAt);
  const now = new Date();
  const diffTime = Math.abs(now - createdDate);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 1) return "امروز عضو شده‌اید";
  if (diffDays < 30) return `${diffDays} روز پیش عضو شده‌اید`;

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} ماه پیش عضو شده‌اید`;

  const diffYears = Math.floor(diffMonths / 12);
  const remainingMonths = diffMonths % 12;

  if (remainingMonths === 0) {
    return `${diffYears} سال پیش عضو شده‌اید`;
  }
  return `${diffYears} سال و ${remainingMonths} ماه پیش عضو شده‌اید`;
}

export default function UserProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
  });

  const [createdAt, setCreatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    document.title = "حساب کاربری";
  }, []);

  // Guard: Protect route on client side
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login", {
        state: { from: location.pathname + location.search },
        replace: true,
      });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  // GET: Fetch user info using authApi.getUser
  useEffect(() => {
    if (!isAuthenticated) return;

    let isMounted = true;
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await authApi.getUser();

        if (isMounted) {
          setFormData({
            first_name: data?.first_name || "",
            last_name: data?.last_name || "",
            phone: data?.phone || "",
          });
          setCreatedAt(data?.created_at || data?.date_joined || null);
        }
      } catch (err) {
        if (isMounted) {
          setError(
            err.response?.data?.detail ||
              err.response?.data?.error ||
              "خطا در دریافت اطلاعات کاربر."
          );
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
    if (success) setSuccess("");
  };

  // PATCH: Submit updated user info using authApi.updateUser
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await authApi.updateUser({
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
      });

      setSuccess("اطلاعات پروفایل با موفقیت بروزرسانی شد.");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          err.response?.data?.phone?.[0] ||
          "خطا در بروزرسانی اطلاعات."
      );
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || (loading && isAuthenticated)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress color="error" />
      </Box>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <Container
      maxWidth="sm"
      sx={{
        mt: { xs: 2, sm: 6 },
        mb: { xs: 10, sm: 6 }, // Bottom margin for mobile sticky button
        px: { xs: 2, sm: 3 },
        direction: "rtl",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: 3, sm: 4 },
          p: { xs: 2.5, sm: 4 },
          background: "#ffffff",
          border: "1px solid",
          borderColor: "grey.200",
          boxShadow: {
            xs: "0px 4px 16px rgba(0, 0, 0, 0.03)",
            sm: "0px 16px 32px rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        {/* Header Section */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2.5}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: { xs: 48, sm: 56 },
                height: { xs: 48, sm: 56 },
                bgcolor: "rgba(239, 64, 86, 0.1)",
                color: "#ef4056",
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                color="text.primary"
                fontSize={{ xs: "1.05rem", sm: "1.25rem" }}
              >
                اطلاعات حساب کاربری
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ویرایش نام، نام خانوادگی و شماره موبایل
              </Typography>
            </Box>
          </Box>

          {/* Registration Duration Badge */}
          {createdAt && (
            <Chip
              icon={<AccessTimeIcon style={{ fontSize: 16 }} />}
              label={formatMemberDuration(createdAt)}
              size="small"
              sx={{
                bgcolor: "grey.100",
                color: "text.secondary",
                fontWeight: 500,
                fontSize: "0.8rem",
                py: 1.5,
                px: 0.5,
                borderRadius: 2,
                "& .MuiChip-icon": {
                  color: "#ef4056",
                  ml: 0.5,
                  mr: -0.5,
                },
              }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Feedback Alerts */}
        {error && (
          <Fade in={!!error}>
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          </Fade>
        )}

        {success && (
          <Fade in={!!success}>
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              {success}
            </Alert>
          </Fade>
        )}

        {/* Form Fields */}
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2.5}>
            {/* First Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="نام"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                variant="outlined"
                placeholder="مثال: علی"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeOutlinedIcon sx={{ color: "grey.400", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5,
                    bgcolor: "grey.50",
                    "&.Mui-focused": { bgcolor: "#fff" },
                  },
                }}
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="نام خانوادگی"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                variant="outlined"
                placeholder="مثال: محمدی"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeOutlinedIcon sx={{ color: "grey.400", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5,
                    bgcolor: "grey.50",
                    "&.Mui-focused": { bgcolor: "#fff" },
                  },
                }}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="شماره همراه"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                variant="outlined"
                placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIphoneIcon sx={{ color: "grey.400", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5,
                    bgcolor: "grey.50",
                    "&.Mui-focused": { bgcolor: "#fff" },
                  },
                  "& input": {
                    direction: "ltr",
                    textAlign: "right",
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Desktop & Tablet Submit Button */}
          <Box sx={{ mt: 3.5, display: { xs: "none", sm: "block" } }}>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              fullWidth
              disableElevation
              startIcon={
                saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
              sx={{
                bgcolor: "#ef4056",
                color: "#ffffff",
                height: 48,
                borderRadius: 2.5,
                fontWeight: 700,
                fontSize: "0.95rem",
                "& .MuiButton-startIcon": {
                  ml: 1,
                  mr: -0.5,
                },
                "&:hover": {
                  bgcolor: "#d73247",
                },
                "&.Mui-disabled": {
                  bgcolor: "grey.200",
                },
              }}
            >
              {saving ? "در حال ذخیره..." : "ثبت و ذخیره اطلاعات"}
            </Button>
          </Box>

          {/* Mobile Sticky Bottom Action Bar */}
          <Box
            sx={{
              display: { xs: "block", sm: "none" },
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              bgcolor: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(8px)",
              borderTop: "1px solid",
              borderColor: "grey.200",
              zIndex: 1100,
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              fullWidth
              disableElevation
              startIcon={
                saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
              sx={{
                bgcolor: "#ef4056",
                color: "#ffffff",
                height: 46,
                borderRadius: 2.5,
                fontWeight: 700,
                fontSize: "0.9rem",
                "& .MuiButton-startIcon": {
                  ml: 1,
                  mr: -0.5,
                },
                "&:hover": {
                  bgcolor: "#d73247",
                },
                "&.Mui-disabled": {
                  bgcolor: "grey.200",
                },
              }}
            >
              {saving ? "در حال ذخیره..." : "ثبت و ذخیره تغییرات"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}